/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1791244800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Oct 06 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "adobe-campaign-classic",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Adobe Campaign Classic",
          "depth": 0,
          "id": "connect-to-adobe-campaign-classic"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Recipients",
          "depth": 1,
          "id": "recipients"
        }, {
          "value": "Custom Resources",
          "depth": 1,
          "id": "custom-resources"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {FeatureFlag, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Use the Adobe Campaign Classic destination to sync recipients and custom resources to Adobe Campaign. The destination supports both Campaign Classic v7 and Campaign v8:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Campaign Classic v7"
          }), " — Recipient syncs write directly to the Campaign database over the SOAP API."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Campaign v8"
          }), " — Direct writes work unchanged. You can also opt each recipients sync into ", _jsx(_components.a, {
            href: "#write-method",
            children: "staging table writes"
          }), ", the ingestion path Adobe recommends for Campaign v8's cloud database. Campaign v8 also requires an ", _jsx(_components.a, {
            href: "#connect-to-adobe-campaign-classic",
            children: "API key"
          }), " on API calls."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#recipients",
                children: _jsx(_components.strong, {
                  children: "Recipients"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Adobe Campaign recipients"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert, All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://experienceleague.adobe.com/en/docs/campaign-classic/using/configuring-campaign-classic/api/data-oriented-apis#write---writecollection--xtk-session-",
                children: "Write recipient data ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#custom-resources",
                children: _jsx(_components.strong, {
                  children: "Custom Resources"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to custom resources in Adobe Campaign"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert, All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://experienceleague.adobe.com/en/docs/campaign/campaign-v8/developer/apis/interacting-with-custom-resources",
                children: "Interact with custom resources ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-adobe-campaign-classic",
        children: "Connect to Adobe Campaign Classic"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch authenticates to Adobe Campaign with OAuth server-to-server credentials. In ", _jsx(_components.a, {
          href: "https://developer.adobe.com/console",
          children: "Adobe Developer Console"
        }), ", create or open a project that includes the Adobe Campaign API with OAuth server-to-server authentication, and copy the credential's values."]
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Adobe Campaign Classic"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". Enter the following fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Server URL"
          }), " — Your Campaign instance's hostname, without ", _jsx(_components.code, {
            children: "https://"
          }), ". For example, ", _jsx(_components.code, {
            children: "myinstance.campaign.adobe.com"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), " — The client ID from your Developer Console credential."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client secret"
          }), " — The client secret from your Developer Console credential."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Additional configuration"
        }), ", two more fields apply to Campaign v8 and custom resources:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization ID"
          }), " — Your Adobe organization ID. Required to sync custom resources."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API Key"
          }), " — Required for Adobe Campaign v8 and for syncing custom resources. For Adobe Developer Console integrations, this is the same value as your client ID. Syncs that write through staging tables automatically use your client ID when you leave this field blank."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Adobe Campaign Classic destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Adobe Campaign Classic destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "recipients",
        children: "Recipients"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to Adobe Campaign recipients in ", _jsx(_components.strong, {
          children: "Upsert"
        }), ", ", _jsx(_components.strong, {
          children: "Update"
        }), ", ", _jsx(_components.strong, {
          children: "Insert"
        }), ", or ", _jsx(_components.strong, {
          children: "All"
        }), " mode."]
      }), "\n", _jsx(_components.h4, {
        id: "write-method",
        children: "Write method"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "How should Hightouch write recipients to Adobe Campaign?"
        }), ", select how the sync delivers data:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Write method"
            }), _jsx(_components.th, {
              children: "Use when"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Direct writes"
              })
            }), _jsx(_components.td, {
              children: "Your instance runs Campaign Classic v7. This is the default."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Staging tables (Campaign v8)"
              })
            }), _jsx(_components.td, {
              children: "Your instance runs Campaign v8, especially for high-volume syncs."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Direct writes send recipients straight to the Campaign database over the SOAP API. They work on both v7 and v8. On Campaign v8's Enterprise (FFDA) architecture, however, Adobe recommends routing API writes through the ", _jsx(_components.a, {
          href: "https://experienceleague.adobe.com/en/docs/campaign/campaign-v8/architecture/ffda/staging",
          children: "staging mechanism ↗"
        }), " to reduce load on the cloud database."]
      }), "\n", _jsxs(_components.p, {
        children: ["Staging tables write recipients to your instance's recipient staging table (", _jsx(_components.code, {
          children: "nms:recipientStg"
        }), ") using Campaign v8's Ingest APIs. An Adobe-managed workflow in your instance then syncs staged rows to the Campaign database, about once per hour."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "A staging table sync reports rows as successful once Adobe accepts them into\nthe staging table. The recipients appear in Campaign after Adobe's staging\nworkflow next runs, so expect up to an hour of delay between a successful sync\nand visible changes in the recipient table."
        })
      }), "\n", _jsx(_components.p, {
        children: "Staging table writes have a few limitations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Group membership mappings aren't supported."
          }), " Hightouch rejects rows in syncs that map the group fields (", _jsx(_components.strong, {
            children: "Group relation table key"
          }), ", ", _jsx(_components.strong, {
            children: "Group name"
          }), ", ", _jsx(_components.strong, {
            children: "Group label"
          }), ", or ", _jsx(_components.strong, {
            children: "Group folder ID"
          }), "). To manage group membership, use direct writes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Field names can't start with an underscore."
          }), " Adobe Campaign reserves underscore-prefixed names, like ", _jsx(_components.code, {
            children: "_key"
          }), " and ", _jsx(_components.code, {
            children: "_operation"
          }), ", for its own ingestion directives, so Hightouch rejects rows in syncs that map them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update mode may insert unmatched rows."
          }), " Campaign v8's ingestion API updates or inserts by key, so rows that don't match an existing recipient may be created as new recipients instead of skipped."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " and ", _jsx(_components.strong, {
          children: "Update"
        }), " modes, you can match rows from your model to recipients in Adobe Campaign on any column in your model and any field in Adobe Campaign. Hightouch uses the field you select as the recipients' ", _jsx(_components.code, {
          children: "_key"
        }), ". Ensure the data types of the model column and Adobe Campaign field you select match. Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " and ", _jsx(_components.strong, {
            children: "All"
          }), " modes, Adobe Campaign automatically generates an\nidentifier for every new record synced, so there is no need to match an\nexisting record."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "All"
          }), " mode, every sync run inserts a new recipient for every row in your\nmodel, including rows that were synced before. Existing recipients are never\nupdated or removed. Use this mode when Adobe Campaign should receive a fresh\nrecord on every run, for example to trigger a campaign on the record's\ncreation date."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can map data from any of your model columns to any recipient attribute via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ". Beyond recipient attributes, you can also map to these built-in fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recipient folder ID"
          }), " — The folder the recipient is filed under."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Group relation table key"
          }), ", ", _jsx(_components.strong, {
            children: "Group name"
          }), ", ", _jsx(_components.strong, {
            children: "Group label"
          }), ", ", _jsx(_components.strong, {
            children: "Group folder ID"
          }), " — The recipient's group membership. Group mappings require the ", _jsx(_components.a, {
            href: "#write-method",
            children: "direct writes"
          }), " method."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. The available options depend on your sync mode:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Available in"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "All modes"
            }), _jsx(_components.td, {
              children: "Keep the recipient in Adobe Campaign with all its synced fields."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: "Clear all the mapped fields, but keep the recipient in Adobe Campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Remove user from group"
              })
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: "Clear only the group label, keeping the recipient's other mapped fields."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: "Delete the synced recipients from Adobe Campaign."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "custom-resources",
        children: "Custom Resources"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Custom Resources syncing"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to a custom resource in your Adobe Campaign instance in ", _jsx(_components.strong, {
          children: "Upsert"
        }), ", ", _jsx(_components.strong, {
          children: "Update"
        }), ", ", _jsx(_components.strong, {
          children: "Insert"
        }), ", or ", _jsx(_components.strong, {
          children: "All"
        }), " mode."]
      }), "\n", _jsxs(_components.p, {
        children: ["Custom Resources syncs use Campaign's REST API rather than the SOAP API, so they require the ", _jsx(_components.strong, {
          children: "Organization ID"
        }), " and ", _jsx(_components.strong, {
          children: "API Key"
        }), " fields in your ", _jsx(_components.a, {
          href: "#connect-to-adobe-campaign-classic",
          children: "destination configuration"
        }), ". Only custom resources linked to the profile table are available through the REST API."]
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Which custom resource would you like to sync data to?"
        }), ", select a custom resource from your Adobe Campaign instance."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " and ", _jsx(_components.strong, {
          children: "Update"
        }), " modes, you can match rows from your model to records on any field of the selected custom resource. Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " and ", _jsx(_components.strong, {
            children: "All"
          }), " modes, Adobe Campaign generates the identifier for\nevery new record, so there is no need to match an existing record. In ", _jsx(_components.strong, {
            children: "All"
          }), "\nmode, every sync run inserts a new record for every row in your model,\nincluding rows that were synced before; existing records are never updated or\nremoved."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings-1",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can map data from any of your model columns to the selected resource's fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ". Hightouch loads the available fields from your custom resource's definition."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-1",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Available in"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "All modes"
            }), _jsx(_components.td, {
              children: "Keep the record with all its synced fields."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Update, Upsert"
            }), _jsx(_components.td, {
              children: "Clear the mapped fields, but keep the record."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: "Delete the synced records from the custom resource."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "parallel-requests",
        children: "Parallel requests"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch sends 10 requests in parallel. Higher values can speed up your sync, but Adobe doesn't document rate limits for this endpoint, so increasing the value may result in throttling."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Adobe Campaign Classic, see\n", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: Other\ndestinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, verify the results in Adobe Campaign:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["With ", _jsx(_components.strong, {
            children: "Direct writes"
          }), ", synced recipients appear in the recipient table as soon as the sync run completes."]
        }), "\n", _jsxs(_components.li, {
          children: ["With ", _jsx(_components.strong, {
            children: "Staging tables (Campaign v8)"
          }), ", synced rows land in the ", _jsx(_components.code, {
            children: "nms:recipientStg"
          }), " staging table first. Adobe's staging workflow syncs them to the Campaign database about once per hour. If recipients still haven't appeared after that, check the staging workflow's status in your Campaign instance."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
